// Board markers (UX §6.2, §6.4, §2.9): the shared contact shadow (ink ellipse at 22%, 70% × 18% of the
// cell), output markers (Play: a function-coloured notch, 10% of the cell; the facing arrow `arrow.<dir>`:
// a cream chevron with an ink outline — its natural box is palette.json art.chevron × 0.72 of that, fitted
// into the square ui.json render.arrowSize frame and turned toward the output side), the mouth decal per
// output side (N/E/S/W overlays; the oblique body is never rotated), the dashed "home" outline of a dragged
// item's origin, the Gallery ammo-queue dots, the baked progress bar (bg / fill, cropped with setCrop at
// runtime), the tap highlight ring (M0 geometry: a 12/128 blaze stroke 8/128 in from the edge — the M6
// input glows tint-fill it), `item.missing` (a red tile with a "?") and (M10) the contents-count digits
// `mark.digit.<d>`: a vector numeral, cream over an ink stroke (never fillText).
import { drawGlyphs } from './glyphs.ts';
import { detailStroke, fillP } from './passes.ts';
import { ellipse, poly, rrect } from './shapes.ts';
import type { Generator, MarkerSpec } from './types.ts';

const TURNS: Readonly<Record<string, number>> = { N: 0, E: 1, S: 2, W: 3 };

export const markerGen: Generator<MarkerSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const W = spec.w * 100;
  const H = spec.h * 100;
  const r = pal.rules;
  const ink = pal.hex('ui.ink');
  c.save();
  c.scale(px / 100, px / 100);
  switch (spec.mark) {
    case 'shadow':
      fillP(
        c,
        ellipse(W / 2, H / 2, (r.contactShadowW * 100) / 2, (r.contactShadowH * 100) / 2),
        pal.rgba('ui.ink', r.contactShadowAlpha),
      );
      break;
    case 'notch': {
      const t = poly([
        [W / 2, 2.5],
        [W - 3, H - 2.5],
        [3, H - 2.5],
      ]);
      detailStroke(c, t, ink, 3);
      fillP(c, t, pal.hex(spec.variant));
      break;
    }
    case 'arrow': {
      // the chevron in its natural box (cw × ch units), scaled to fit the square frame, turned to `variant`
      const cw = pal.art.chevron * 100;
      const ch = cw * 0.72;
      const k = Math.min(W / cw, H / ch);
      c.translate(W / 2, H / 2);
      c.rotate(((TURNS[spec.variant] ?? 0) * Math.PI) / 2);
      c.scale(k, k);
      c.translate(-cw / 2, -ch / 2);
      const pts = [
        [5, ch - 6],
        [cw / 2, 6],
        [cw - 5, ch - 6],
      ] as const;
      const path = (cc: Parameters<typeof detailStroke>[0]) => {
        cc.moveTo(pts[0][0], pts[0][1]);
        cc.lineTo(pts[1][0], pts[1][1]);
        cc.lineTo(pts[2][0], pts[2][1]);
      };
      detailStroke(c, path, ink, 10);
      detailStroke(c, path, pal.hex('ui.cream'), 5.5);
      break;
    }
    case 'highlight': {
      const k = W / 128;
      c.strokeStyle = pal.hex('toy.blaze');
      c.lineWidth = 12 * k;
      c.strokeRect(8 * k, 8 * k, W - 16 * k, H - 16 * k);
      c.globalAlpha = 0.18;
      c.fillStyle = pal.hex('toy.blaze');
      c.fillRect(14 * k, 14 * k, W - 28 * k, H - 28 * k);
      c.globalAlpha = 1;
      break;
    }
    case 'digit':
      // a bold glyph, 68% of the frame height; its ink stroke stays inside the frame
      drawGlyphs(c, spec.variant, W / 2, H / 2, H * 0.68, pal.hex('ui.cream'), ink, H * 0.13);
      break;
    case 'missing': {
      const bad = pal.sw('glow.invalid');
      fillP(c, rrect(16, 16, W - 32, H - 32, 12), bad.outline);
      fillP(c, rrect(20, 20, W - 40, H - 40, 9), bad.base);
      drawGlyphs(c, '?', W / 2, H / 2, H * 0.4, pal.hex('ui.cream'), ink, 3);
      break;
    }
    case 'mouth': {
      // Opening on the side of the ¾ body the machine outputs to.
      const m = pal.art.machineInset * 100;
      const yf = m + (100 - 2 * m) * env.sprites.machineTop;
      const lip = pal.sw('mach.front');
      const hole = pal.hex('mach.outline');
      const rect = (x: number, y: number, w: number, h: number) => {
        fillP(c, rrect(x - 1.5, y - 1.5, w + 3, h + 3, 4), lip.outline);
        fillP(c, rrect(x, y, w, h, 3), lip.light);
        fillP(c, rrect(x + 2.2, y + 2.2, w - 4.4, h - 4.4, 2), hole);
      };
      if (spec.variant === 'S') rect(36, yf + 4, 28, 100 - m - yf - 7);
      else if (spec.variant === 'N') rect(36, m - 1, 28, 10);
      else if (spec.variant === 'E') rect(100 - m - 9, 28, 10, 26);
      else rect(m - 1, 28, 10, 26);
      break;
    }
    case 'home': {
      c.setLineDash([8, 6]);
      detailStroke(c, rrect(8, 8, 84, 84, 14), pal.rgba('ui.cream', 0.75), 3.2);
      c.setLineDash([]);
      break;
    }
    case 'dot': {
      const full = spec.variant === '1';
      fillP(c, ellipse(W / 2, H / 2, W / 2 - 1, H / 2 - 1), ink);
      fillP(
        c,
        ellipse(W / 2, H / 2, W / 2 - 2.6, H / 2 - 2.6),
        full ? pal.hex('ui.money') : pal.hex('mach.front'),
      );
      if (full) fillP(c, ellipse(W / 2 - 1.2, H / 2 - 1.2, 1.4, 1.1), pal.sw('ui.money').hi);
      break;
    }
    default: {
      // progress bar: bg = ink trough, fill = flow green
      const bg = spec.variant === 'bg';
      fillP(c, rrect(1, 1, W - 2, H - 2, (H - 2) / 2), bg ? ink : pal.hex('state.flowOk'));
      if (!bg)
        fillP(c, rrect(3, 2, W - 6, (H - 4) * 0.36, (H - 4) * 0.18), pal.sw('state.flowOk').hi);
    }
  }
  c.restore();
};
